import { useCallback, useEffect, useState } from 'react';
import errorMonitoring from '@/errorMonitoring';
import { fetchStoredCards, type StoredCard } from 'services/storedCards';
import { setUserProperties } from 'user-analytics';

type StoredCardsQueryState =
  | { status: 'disabled' }
  | { status: 'loading' }
  | { status: 'empty' }
  | { status: 'error' }
  | { status: 'success'; cards: StoredCard[] };

interface UseStoredCardsOptions {
  enabled: boolean;
  sessionKey: string;
}

interface UseStoredCardsResult {
  query: StoredCardsQueryState;
  reload: () => void;
}

interface SessionOwnedQuery {
  query: StoredCardsQueryState;
  sessionKey: string;
}

/** Own the authenticated stored-card query and ignore stale session responses. */
function useStoredCards({ enabled, sessionKey }: UseStoredCardsOptions): UseStoredCardsResult {
  const [reloadKey, setReloadKey] = useState(0);
  const [ownedQuery, setOwnedQuery] = useState<SessionOwnedQuery>({
    query: { status: 'disabled' },
    sessionKey: '',
  });

  let query: StoredCardsQueryState;
  if (!enabled || !sessionKey) query = { status: 'disabled' };
  else if (ownedQuery.sessionKey !== sessionKey) query = { status: 'loading' };
  else query = ownedQuery.query;

  useEffect(() => {
    if (!enabled || !sessionKey) {
      setOwnedQuery({ query: { status: 'disabled' }, sessionKey: '' });
      return undefined;
    }

    let cancelled = false;
    setOwnedQuery({ query: { status: 'loading' }, sessionKey });

    fetchStoredCards()
      .then((cards) => {
        if (cancelled) return;
        setUserProperties({
          'Saved Cards Count': cards.length,
          'Expired Saved Cards Count': cards.filter((card) => card.isExpired).length,
        });
        setOwnedQuery({
          query: cards.length ? { status: 'success', cards } : { status: 'empty' },
          sessionKey,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        errorMonitoring.notify({ error, info: { stage: 'stored_cards_fetch' } });
        setOwnedQuery({ query: { status: 'error' }, sessionKey });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, reloadKey, sessionKey]);

  const reload = useCallback(() => setReloadKey((currentKey) => currentKey + 1), []);
  return { query, reload };
}

export type { StoredCardsQueryState };
export { useStoredCards };
